import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Video, Trash2, Edit2, GripVertical, ChevronDown, ChevronUp, Save, X, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useTrainingModules } from '../hooks/useTrainingModules';
import { QuizEditor } from './QuizEditor';
import type { TrainingModule } from '../types';

export const ModuleManager: React.FC = () => {
  const { modules, isLoading, createModule, updateModule, deleteModule } = useTrainingModules();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    is_active: true,
  });

  const handleCreate = async () => {
    if (!formData.title || !formData.video_url) return;
    
    await createModule.mutateAsync({
      title: formData.title,
      description: formData.description || null,
      video_url: formData.video_url,
      order_position: modules.length,
      is_active: formData.is_active,
      created_by: null,
    });
    
    setFormData({ title: '', description: '', video_url: '', is_active: true });
    setIsCreating(false);
  };

  const handleUpdate = async (id: string) => {
    await updateModule.mutateAsync({
      id,
      title: formData.title,
      description: formData.description || null,
      video_url: formData.video_url,
      is_active: formData.is_active,
    });
    setEditingId(null);
  };

  const startEdit = (module: TrainingModule) => {
    setFormData({
      title: module.title,
      description: module.description || '',
      video_url: module.video_url,
      is_active: module.is_active,
    });
    setEditingId(module.id);
  };

  const getVideoEmbedUrl = (url: string) => {
    // Convert YouTube URLs to embed format
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    // Convert Vimeo URLs to embed format
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    // Convert Loom URLs to embed format
    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) {
      return `https://www.loom.com/embed/${loomMatch[1]}`;
    }
    return url;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold">Módulos de Entrenamiento</h3>
          <p className="text-white/50 text-sm">{modules.length} módulo{modules.length !== 1 ? 's' : ''} creado{modules.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => setIsCreating(true)}
          className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30"
          disabled={isCreating}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Módulo
        </Button>
      </div>

      {/* Create Form */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 space-y-3"
          >
            <Input
              placeholder="Título del módulo"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <Textarea
              placeholder="Descripción (opcional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              rows={2}
            />
            <Input
              placeholder="URL del video (YouTube, Vimeo o Loom)"
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-white/60 text-sm">Activo</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setFormData({ title: '', description: '', video_url: '', is_active: true });
                  }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={!formData.title || !formData.video_url || createModule.isPending}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Guardar
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modules List */}
      <div className="space-y-2">
        {modules.map((module, index) => (
          <motion.div
            key={module.id}
            layout
            className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
          >
            {editingId === module.id ? (
              <div className="p-4 space-y-3">
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  rows={2}
                />
                <Input
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <span className="text-white/60 text-sm">Activo</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleUpdate(module.id)}
                      disabled={updateModule.isPending}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 flex items-center gap-3">
                  <div className="text-white/30 cursor-grab">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-medium truncate">{module.title}</h4>
                      {!module.is_active && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded">
                          Inactivo
                        </span>
                      )}
                    </div>
                    {module.description && (
                      <p className="text-white/50 text-sm truncate">{module.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedId(expandedId === module.id ? null : module.id)}
                      className="text-white/50 hover:text-white hover:bg-white/10"
                    >
                      <HelpCircle className="w-4 h-4 mr-1" />
                      Quiz
                      {expandedId === module.id ? (
                        <ChevronUp className="w-4 h-4 ml-1" />
                      ) : (
                        <ChevronDown className="w-4 h-4 ml-1" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(module)}
                      className="text-white/50 hover:text-white hover:bg-white/10"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteModule.mutate(module.id)}
                      className="text-red-400/50 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Video Preview & Quiz */}
                {expandedId === module.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-white/10"
                  >
                    <div className="p-4 space-y-4">
                      {/* Video Preview - Constrained for desktop */}
                      <div className="w-full max-w-2xl mx-auto">
                        <div className="aspect-video bg-black/50 rounded-lg overflow-hidden">
                          <iframe
                            src={getVideoEmbedUrl(module.video_url)}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                      
                      {/* Quiz Editor */}
                      <QuizEditor moduleId={module.id} />
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        ))}

        {modules.length === 0 && !isCreating && (
          <div className="text-center py-12 text-white/50">
            <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay módulos creados</p>
            <p className="text-sm">Crea tu primer módulo de entrenamiento</p>
          </div>
        )}
      </div>
    </div>
  );
};
